/**
 * @component RegisterCorporatePartner
 * @description
 * This component manages the creation or editing of a corporate partner (customer)
 * and the selection of related offers through a multi-step form flow.
 *
 * The process consists of:
 *  Entering corporate details
 *   Selecting or creating offers
 *   Reviewing confirmation before saving
 *
 * Once all steps are completed, it sends the data to the backend to
 * create or update corporate partner details along with associated offers.
 */

import React, { useState, useEffect } from 'react';
import StepTimeline from '../step-timeline/step-timeline';
import { Form, Select, message, Divider } from 'antd';

import { Card, Location } from 'soxo-bootstrap-core';

import AppLoader from '../../../../components/app-loader';

import { Items, Customers } from '../../../../models';

import './register-corporate-partner.scss';

import CorporateDetail from '../../../corporate-details/corporate-detail';
import SelectOffers from '../../../select-offers/select-offers';
import CorporateOfferConfirmation from '../../../corporate-offer-confirmation/corporate-offer-confirmation';


const RegisterCorporatePartner = (match) => {
    //  Holds corporate form data
    const [corporateDetails, setCorporateDetails] = useState({});
    // sleected offer lists
    const [selectedOffers, setSelectedOffers] = useState([]);
    // loading
    const [loading, setLoading] = useState(false);
    // store fetched custmer data
    const [customerData, setCustomerData] = useState(null);

    // Extract `cc_code` from the URL query params (used to fetch existing corporate data)
    const urlParams = new URLSearchParams(Location.search());
    const cc_code = urlParams.get('cc_code');

    // const [selectedOffers, setSelectedOffers] = useState([]);

    const [form] = Form.useForm();

    /**
     *
     *
     *
     * If a corporate code (cc_code) exists in the URL, fetch its data
     * */

    useEffect(() => {
        if (cc_code) {
            fetchCustomerData(cc_code);
        }
    }, [cc_code]);

    /**
     *
     * Fetch corporate customer data and associated referrer mapping
     */

    const fetchCustomerData = async (code) => {
        try {
            setLoading(true);
            // Fetch customer details
            const result = await Customers.getCustomerWithId(code);

            if (result.result.cc_otherdetail1) {
                // Parse additional details (JSON string to object)
                const otherDetails = JSON.parse(result.result.cc_otherdetail1);
                result.result.cc_otherdetail1 = otherDetails.idCardUpload;
            }
            // Fetch corporate referrer mapping
            const referrer = await Items.getCorporateReferrerMapping(code);

            // Merge all data
            const data = {
                ...result.result,
                referrer: referrer?.id || null,
            };
            // Update states and form values
            setCustomerData(data);
            setCorporateDetails(data);
            form.setFieldsValue(data);
        } catch (err) {
            // console.error(err);
            // message.error('Failed to load customer data');
        } finally {
            setLoading(false);
        }
    };

    /**
     
     * Triggered when corporate detail form fields change
     */

    const handleCorporateChange = (changedValues, allValues) => {
        setCorporateDetails(allValues);
    };

    /**
     
     * 
     * Called from the SelectOffers component when offers are selected
     */
    const handleOfferSelection = (offers) => {
        setSelectedOffers(offers);
    };

    /**
   
     * Constructs the payload (customer + offers) and sends it to back
     */

    const handleFinish = async () => {
        try {
            // Prepare customer (corporate) details
            const customer = {
                cc_code: corporateDetails.cc_code,
                cc_mode: corporateDetails.cc_mode || 'CCUS',
                cc_desc: corporateDetails.cc_desc,
                cc_type: corporateDetails.cc_type || 'PYR',
                cc_active: corporateDetails.cc_active || 'Y',
                cc_otherdetail1: JSON.stringify({
                    idCardUpload: corporateDetails.idCardUpload || 'MND',
                }),
                referrer: corporateDetails.referrer || '1',
                cc_styledcontent: corporateDetails.cc_styledcontent || '',
            };

            // Prepare offer details (array of offer objects)

            const offer_details = selectedOffers.map((offer, index) => ({
                corporate_pointer: corporateDetails.cc_code,
                offer_code: offer.code,
                slno: index + 1,
                other_details: {
                    valid_from: offer.validity?.[0]?.toISOString?.() || null,
                    valid_to: offer.validity?.[1]?.toISOString?.() || null,
                },
            }));

            const payload = { customer, offer_details };
            console.log('Final Payload:', payload);

            const response = await Items.createCorporateDetails(payload);

            if (response?.result?.success) {
                //  Return true so ConfirmInformation knows it's successful
                message.success(response.result.message);
                // Open the modal here
                // setIsModalVisible(true); // <-- You need to lift this state up
                Location.navigate({ url: `/corporate-master` }); //NAVIGATE TO CORPORATE MASTER LIST
                return true;
            } else {
                message.error(response?.result?.message);
                return false;
            }
        } catch (error) {
            message.error('An error occurred while saving corporate offers');
            return false;
        }
    };

    /**
    
     * Defines the 3-step flow for corporate registration
     */
    const steps = [
        {
            title: 'Corporate Details',
            description: 'Enter Corporate details',
            content: <CorporateDetail onValuesChange={handleCorporateChange} initialValues={corporateDetails} />,
        },
        {
            title: 'Select Offers',
            description: 'Select or create offers',
            content: (
                <SelectOffers onSelectionChange={handleOfferSelection} corporateDetails={corporateDetails} initialSelectedOffers={selectedOffers} />
            ),
        },
        {
            title: 'Confirmation',
            description: 'Confirm details and save',
            content: (
                <CorporateOfferConfirmation
                    corporateDetails={corporateDetails}
                    selectedOffers={selectedOffers}
                    setSelectedOffers={setSelectedOffers}
                />
            ),
        },
    ];

    return (
        // <div className="register-corporate-wrapper">
        <>
            <AppLoader loading={loading} /> {/* Wrap content with loader */}
            <Card className="register-corporate-card">
                <StepTimeline steps={steps} onComplete={handleFinish} />
            </Card>
        </>
    );
};

export default RegisterCorporatePartner;
